import { updateChannelMember } from 'flow/actions/channels-actions';
import { updateProfiles } from 'flow/actions/profile-actions';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useChannel from 'flow/hooks/useChannel';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { ChannelKey, ExtensionKey } from 'flow/types/EntityKeys';
import { ChannelLoginList, Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { getChannelLoggedInValueForProfile } from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import { fromJS, List, Map } from 'immutable';
import { useParams } from 'react-router-dom';
import SettingRow from '../SettingRow';
import SettingSection from '../SettingSection';

interface Props {
    perProfile: boolean;
}

const LiveChats = ({ perProfile }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const { channels, channelNames } = useChannel(extensionKey);
    const { extension } = useExtension(extensionKey);
    const { profiles } = useProfiles(extensionKey);

    const myChannelLogins = profiles.first().get('channelLoginList');

    if (!myChannelLogins?.size) {
        return null;
    }

    const getDescription = (channelLogin: TypedMap<ChannelLoginList>) => {
        const loggedInProfilesForChannels = myChannelLogins.reduce(
            (loginsPerChannel, channelLogin) => {
                const channelKey = channelLogin.get('channelEntityKey');
                const numberOfLoggedInProfiles = profiles.count((profile) =>
                    getChannelLoggedInValueForProfile(profile, channelKey),
                );

                return loginsPerChannel.set(
                    channelKey,
                    numberOfLoggedInProfiles,
                );
            },
            Map(),
        );

        const enabledProfiles = loggedInProfilesForChannels.get(
            channelLogin.get('channelEntityKey'),
        );

        if (enabledProfiles === 0) {
            return i18next.t('settings.off');
        }

        if (enabledProfiles === profiles.size) {
            return i18next.t('settings.on');
        }

        return i18next.t('settings.number_of_profiles', {
            activeCount: enabledProfiles,
            profileCount: profiles.size,
        });
    };

    const handleChange = (key: ChannelKey, value: boolean) => {
        const profilesWithUpdatedChannelLogins = updateProfileListFromChannel(
            profiles,
            key,
            value,
        );

        const updatedProfileList = profilesWithUpdatedChannelLogins.map(
            (profile) =>
                fromJS({
                    key: profile.get('key'),
                    channelLoginList: profile.get('channelLoginList'),
                }),
        );

        dispatch(updateProfiles(extensionKey, updatedProfileList));

        const memberDTO = channels
            .getIn([key, 'channelMemberDTOs'])
            .find(
                (member) =>
                    member.get('chatUserKey') === extension.get('chatUserKey'),
            );
        const updatedMemberDTO = memberDTO.set('loggedIn', value);
        dispatch(
            updateChannelMember(
                key,
                updatedMemberDTO.get('key'),
                updatedMemberDTO,
            ),
        );
    };

    const updateProfileListFromChannel = (
        profileList: List<TypedMap<Profile>>,
        channelKey: ChannelKey,
        newValue: boolean,
    ) =>
        profileList.map((profile) =>
            profile.update(
                'channelLoginList',
                List<TypedMap<ChannelLoginList>>(),
                (prevChannelLogins: List<TypedMap<ChannelLoginList>>) => {
                    const indexOfChannelToUpdate = prevChannelLogins.findIndex(
                        (login: TypedMap<ChannelLoginList>) =>
                            login.get('channelEntityKey') === channelKey,
                    );
                    if (indexOfChannelToUpdate === -1) {
                        // This profile controlled no channel logins. We'll create one.
                        return prevChannelLogins.push(
                            fromJS({
                                channelEntityKey: channelKey,
                                loggedIn: newValue,
                            }),
                        );
                    }
                    return prevChannelLogins.setIn(
                        [indexOfChannelToUpdate, 'loggedIn'],
                        newValue,
                    );
                },
            ),
        );

    return (
        <>
            <SettingSection className="text-sm/4">
                {i18next.t('settings.queue_channel_login.external_channels')}
            </SettingSection>

            {myChannelLogins
                .sort((a, b) =>
                    channelNames
                        .get(a.get('channelEntityKey'))
                        ?.localeCompare(
                            channelNames.get(b.get('channelEntityKey')),
                        ),
                )
                .map((channelLogin) => {
                    if (perProfile) {
                        return (
                            <RouteLink
                                key={channelLogin.get('key')}
                                to={generatePath(
                                    routes[
                                        'settings.queue-and-chat-login.channelKey'
                                    ],
                                    {
                                        extensionKey,
                                        channelKey:
                                            channelLogin.get(
                                                'channelEntityKey',
                                            ),
                                    },
                                )}
                                description={getDescription(channelLogin)}
                            >
                                {channelNames.get(
                                    channelLogin.get('channelEntityKey'),
                                )}
                            </RouteLink>
                        );
                    }

                    return (
                        <SettingRow
                            key={channelLogin.get('key')}
                            cta={
                                <Toggle
                                    value={channelLogin.get('loggedIn', false)}
                                    onChange={(value) =>
                                        handleChange(
                                            channelLogin.get(
                                                'channelEntityKey',
                                            ),
                                            value,
                                        )
                                    }
                                    text={channelNames.get(
                                        channelLogin.get('channelEntityKey'),
                                    )}
                                />
                            }
                        >
                            {channelNames.get(
                                channelLogin.get('channelEntityKey'),
                            )}
                        </SettingRow>
                    );
                })}
        </>
    );
};

export default LiveChats;
